// Control Panel → Localization → Internal Schedule (Luma 2026-08-26).
// Editable phase rows (name · weeks · milestones) plus the AI draft:
// drop a spreadsheet/PDF, POST it to #generate, and rebuild the rows
// from the returned template. Nothing persists until the form saves.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "list", "row", "rowTemplate", "empty",
    "dropzone", "dropzoneHint", "loading", "fileInput", "error",
    "sourceFilename"
  ]
  static values = { generateUrl: String }

  addRow(event) {
    event?.preventDefault()
    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    this.listTarget.appendChild(fragment)
    this._syncEmpty()
    const row = this.listTarget.lastElementChild
    row?.querySelector("input[type=text]")?.focus()
    return row
  }

  removeRow(event) {
    event.preventDefault()
    event.currentTarget.closest('[data-internal-schedule-target="row"]')?.remove()
    this._syncEmpty()
  }

  // ── AI draft ───────────────────────────────────────────────────────

  browse(event) {
    event?.preventDefault()
    this.fileInputTarget.click()
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-brand", "bg-blue-50/30")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50/30")
  }

  drop(event) {
    event.preventDefault()
    this.dragLeave()
    const file = event.dataTransfer?.files?.[0]
    if (file) this._generate(file)
  }

  fileChosen(event) {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ""
    if (file) this._generate(file)
  }

  async _generate(file) {
    this._setLoading(true)
    this.errorTarget.classList.add("hidden")

    const form = new FormData()
    form.append("file", file)

    let resp
    try {
      const raw = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "Accept": "application/json"
        },
        credentials: "same-origin",
        body: form
      })
      resp = { ok: raw.ok, body: await raw.json().catch(() => null) }
    } catch (err) {
      resp = { ok: false, body: { error: err.message } }
    }

    this._setLoading(false)
    if (!resp.ok) {
      this.errorTarget.textContent = resp.body?.error || "We couldn't read that file."
      this.errorTarget.classList.remove("hidden")
      return
    }

    this._fillRows(resp.body.items || [])
    if (this.hasSourceFilenameTarget) {
      this.sourceFilenameTarget.value = resp.body.source_filename || file.name
    }
  }

  // The AI draft REPLACES the current rows — the admin asked for a
  // fresh read of the file; hand edits belong after, not merged.
  _fillRows(items) {
    this.rowTargets.forEach(row => row.remove())
    items.forEach(item => {
      const row = this.addRow()
      if (!row) return
      row.querySelector('input[name="internal_schedule[item_names][]"]').value = item.name || ""
      row.querySelector('input[name="internal_schedule[item_weeks][]"]').value = item.weeks ?? ""
      row.querySelector('textarea[name="internal_schedule[item_milestones][]"]').value =
        (item.milestones || []).join("\n")
    })
    this._syncEmpty()
  }

  _setLoading(on) {
    this.dropzoneHintTarget.classList.toggle("hidden", on)
    this.loadingTarget.classList.toggle("hidden", !on)
    this.loadingTarget.classList.toggle("flex", on)
  }

  _syncEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", this.rowTargets.length > 0)
    }
  }
}
